import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { 
  Palette, 
  Bell, 
  Shield, 
  Settings2, 
  Save,
  Loader2,
  BarChart3,
  Trash2,
  Phone,
  MessageSquare,
  Mail,
  HelpCircle,
  Gift,
  Headphones,
  BellRing,
  User,
  Camera,
  Upload,
  X,
  Send,
  ExternalLink,
  CheckCircle2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Settings = () => {
  const { user, updateAvatar } = useAuth();
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [testingTelegram, setTestingTelegram] = useState(false);
  const [fetchingChatId, setFetchingChatId] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error(language === 'fr' ? 'Fichier trop volumineux (max 2MB)' : 'File too large (max 2MB)');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(language === 'fr' ? 'Veuillez sélectionner une image' : 'Please select an image');
      return;
    }

    setUploadingAvatar(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/auth/avatar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      updateAvatar(response.data.avatar_url);
      toast.success(language === 'fr' ? 'Avatar mis à jour !' : 'Avatar updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteAvatar = async () => {
    try {
      await axios.delete(`${API}/auth/avatar`);
      updateAvatar(null);
      toast.success(language === 'fr' ? 'Avatar supprimé' : 'Avatar deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings);
      toast.success(t('saved'));
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestTelegram = async () => {
    if (!settings.telegram_bot_token || !settings.telegram_chat_id) {
      toast.error(language === 'fr' ? 'Veuillez remplir le token et le chat ID' : 'Please fill in token and chat ID');
      return;
    }
    
    setTestingTelegram(true);
    try {
      await axios.post(`${API}/telegram/test`, {
        bot_token: settings.telegram_bot_token,
        chat_id: settings.telegram_chat_id
      });
      toast.success(language === 'fr' ? '✅ Message Telegram envoyé !' : '✅ Telegram message sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Telegram test failed');
    } finally {
      setTestingTelegram(false);
    }
  };

  const handleFetchChatId = async () => {
    if (!settings.telegram_bot_token) {
      toast.error(language === 'fr' ? 'Veuillez d\'abord entrer le token du bot' : 'Please enter bot token first');
      return;
    }
    
    setFetchingChatId(true);
    try {
      const response = await axios.get(`${API}/telegram/get-chat-id/${encodeURIComponent(settings.telegram_bot_token)}`);
      if (response.data.chats && response.data.chats.length > 0) {
        const chat = response.data.chats[0];
        updateSettings('telegram_chat_id', chat.chat_id);
        toast.success(language === 'fr' ? `Chat ID trouvé: ${chat.chat_id} (${chat.name})` : `Chat ID found: ${chat.chat_id} (${chat.name})`);
      } else {
        toast.info(language === 'fr' ? 'Envoyez d\'abord un message à votre bot Telegram' : 'Send a message to your bot first');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch chat ID');
    } finally {
      setFetchingChatId(false);
    }
  };

  const updateSettings = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const tabs = [
    { id: 'profile', label: language === 'fr' ? 'Profil' : 'Profile', icon: User },
    { id: 'popup', label: 'Popup', icon: Palette },
    { id: 'notifications', label: t('notifications'), icon: Bell },
    { id: 'gdpr', label: 'RGPD', icon: Shield },
    { id: 'behavior', label: t('behaviorSettings'), icon: Settings2 },
  ];

  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#0a0a0f]' : 'bg-gray-50'}`}>
        <Navbar />
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-[#3272F1]" />
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0a0a0f]' : 'bg-gray-50'}`} style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Navbar />
      
      {/* Background effects */}
      {isDark && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#3272F1]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-600/5 rounded-full blur-3xl"></div>
        </div>
      )}
      
      <main className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className={`text-3xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('settings')}
            </h1>
            <p className={`mt-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
              {user?.company_name}
            </p>
          </div>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-[#3272F1] hover:bg-[#2560d9] gap-2"
            data-testid="save-settings-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? t('saving') : t('save')}
          </Button>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Tabs */}
          <div className="lg:w-56 flex-shrink-0">
            <div className={`${isDark ? 'bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800' : 'bg-white border-gray-200 shadow-sm'} rounded-2xl border p-2`}>
              {tabs.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    activeTab === id
                      ? 'bg-[#3272F1] text-white'
                      : isDark 
                        ? 'text-gray-400 hover:text-white hover:bg-gray-800'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                  data-testid={`tab-${id}`}
                >
                  <Icon className="w-5 h-5" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1">
            <div className={`${isDark ? 'bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800' : 'bg-white border-gray-200 shadow-sm'} rounded-2xl border p-6 lg:p-8`}>
              
              {/* Profile Settings */}
              {activeTab === 'profile' && (
                <div className="space-y-8">
                  <div className={`pb-6 border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                    <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {language === 'fr' ? 'Profil & Logo' : 'Profile & Logo'}
                    </h2>
                    <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                      {language === 'fr' ? 'Personnalisez votre avatar ou ajoutez le logo de votre entreprise' : 'Customize your avatar or add your company logo'}
                    </p>
                  </div>

                  {/* Avatar Upload Section */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                    {/* Avatar Preview */}
                    <div className="relative group">
                      <div className={`w-24 h-24 rounded-2xl overflow-hidden border-2 ${isDark ? 'border-gray-700 bg-white' : 'border-gray-200 bg-white'} flex items-center justify-center`}>
                        {user?.avatar_url ? (
                          <img 
                            src={`${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}`}
                            alt="Avatar"
                            className="w-full h-full object-contain p-1"
                          />
                        ) : (
                          <User className={`w-10 h-10 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                        )}
                      </div>
                      {/* Overlay on hover */}
                      <div 
                        className="absolute inset-0 rounded-2xl bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Camera className="w-6 h-6 text-white" />
                      </div>
                    </div>

                    {/* Upload Controls */}
                    <div className="flex-1 space-y-3">
                      <div>
                        <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {language === 'fr' ? 'Avatar / Logo entreprise' : 'Avatar / Company logo'}
                        </h3>
                        <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                          {language === 'fr' ? 'JPG, PNG, GIF ou SVG. Max 2MB.' : 'JPG, PNG, GIF or SVG. Max 2MB.'}
                        </p>
                      </div>
                      
                      <div className="flex gap-3">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarUpload}
                          className="hidden"
                        />
                        <Button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingAvatar}
                          className="bg-[#3272F1] hover:bg-[#2560d9] text-white gap-2"
                          data-testid="upload-avatar-btn"
                        >
                          {uploadingAvatar ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4" />
                          )}
                          {language === 'fr' ? 'Télécharger' : 'Upload'}
                        </Button>
                        {user?.avatar_url && (
                          <Button
                            onClick={handleDeleteAvatar}
                            variant="outline"
                            className={`gap-2 ${isDark ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-200 text-red-600 hover:bg-red-50'}`}
                            data-testid="delete-avatar-btn"
                          >
                            <Trash2 className="w-4 h-4" />
                            {language === 'fr' ? 'Supprimer' : 'Delete'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Company Info (read-only for now) */}
                  <div className={`pt-6 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                    <h3 className={`font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {language === 'fr' ? 'Informations du compte' : 'Account information'}
                    </h3>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <Label className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                          {language === 'fr' ? 'Entreprise' : 'Company'}
                        </Label>
                        <p className={`mt-1 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {user?.company_name}
                        </p>
                      </div>
                      <div>
                        <Label className={isDark ? 'text-gray-400' : 'text-gray-600'}>Email</Label>
                        <p className={`mt-1 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {user?.email}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Popup Settings */}
              {activeTab === 'popup' && (
                <div className="space-y-8">
                  <div className={`flex items-center justify-between pb-6 border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                    <div>
                      <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('popupSettings')}</h2>
                      <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Personnalisez l'apparence de votre popup</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                        {settings.popup_enabled ? t('enabled') : t('disabled')}
                      </span>
                      <Switch
                        checked={settings.popup_enabled}
                        onCheckedChange={(checked) => updateSettings('popup_enabled', checked)}
                        data-testid="popup-enabled-switch"
                      />
                    </div>
                  </div>

                  {/* French texts */}
                  <div className="space-y-4">
                    <h3 className="font-medium text-[#3272F1] text-sm uppercase tracking-wider">Français</h3>
                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>{t('popupTitle')}</Label>
                        <Input
                          value={settings.popup_title_fr}
                          onChange={(e) => updateSettings('popup_title_fr', e.target.value)}
                          className={isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}
                          data-testid="popup-title-fr"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>{t('popupMessage')}</Label>
                        <Textarea
                          value={settings.popup_message_fr}
                          onChange={(e) => updateSettings('popup_message_fr', e.target.value)}
                          rows={2}
                          className={isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}
                          data-testid="popup-message-fr"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>{t('ctaButton')}</Label>
                        <Input
                          value={settings.popup_cta_fr}
                          onChange={(e) => updateSettings('popup_cta_fr', e.target.value)}
                          className={isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}
                          data-testid="popup-cta-fr"
                        />
                      </div>
                    </div>
                  </div>

                  {/* English texts */}
                  <div className={`space-y-4 pt-6 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                    <h3 className="font-medium text-[#3272F1] text-sm uppercase tracking-wider">English</h3>
                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>{t('popupTitle')}</Label>
                        <Input
                          value={settings.popup_title_en}
                          onChange={(e) => updateSettings('popup_title_en', e.target.value)}
                          className={isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}
                          data-testid="popup-title-en"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>{t('popupMessage')}</Label>
                        <Textarea
                          value={settings.popup_message_en}
                          onChange={(e) => updateSettings('popup_message_en', e.target.value)}
                          rows={2}
                          className={isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}
                          data-testid="popup-message-en"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>{t('ctaButton')}</Label>
                        <Input
                          value={settings.popup_cta_en}
                          onChange={(e) => updateSettings('popup_cta_en', e.target.value)}
                          className={isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}
                          data-testid="popup-cta-en"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Color picker */}
                  <div className={`space-y-3 pt-6 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>{t('primaryColor')}</Label>
                    <div className="flex items-center gap-4">
                      <input
                        type="color"
                        value={settings.primary_color || '#3272F1'}
                        onChange={(e) => updateSettings('primary_color', e.target.value)}
                        className={`w-14 h-14 rounded-xl cursor-pointer border-2 ${isDark ? 'border-gray-700' : 'border-gray-300'} bg-transparent`}
                        data-testid="primary-color-input"
                      />
                      <Input
                        value={settings.primary_color || '#3272F1'}
                        onChange={(e) => updateSettings('primary_color', e.target.value)}
                        className={`w-32 font-mono ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                    </div>
                  </div>

                  {/* Widget Position */}
                  <div className={`space-y-3 pt-6 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>{language === 'fr' ? 'Position du widget' : 'Widget position'}</Label>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { value: 'bottom-right', label: language === 'fr' ? 'Bas droite' : 'Bottom right' },
                        { value: 'bottom-left', label: language === 'fr' ? 'Bas gauche' : 'Bottom left' },
                        { value: 'top-right', label: language === 'fr' ? 'Haut droite' : 'Top right' },
                        { value: 'top-left', label: language === 'fr' ? 'Haut gauche' : 'Top left' }
                      ].map(({ value, label }) => (
                        <button
                          key={value}
                          onClick={() => updateSettings('widget_position', value)}
                          className={`p-4 rounded-xl border-2 text-sm font-medium transition-all ${
                            (settings.widget_position || 'bottom-right') === value
                              ? 'border-[#3272F1] bg-[#3272F1]/10 text-[#3272F1]'
                              : isDark 
                                ? 'border-gray-700 text-gray-400 hover:border-gray-600 hover:text-gray-300'
                                : 'border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-700'
                          }`}
                          data-testid={`position-${value}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Button Icon */}
                  <div className={`space-y-3 pt-6 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>{language === 'fr' ? 'Icône du bouton' : 'Button icon'}</Label>
                    <div className="grid grid-cols-4 sm:grid-cols-7 gap-3">
                      {[
                        { value: 'phone', icon: Phone, label: language === 'fr' ? 'Téléphone' : 'Phone' },
                        { value: 'chat', icon: MessageSquare, label: 'Chat' },
                        { value: 'message', icon: Mail, label: language === 'fr' ? 'Message' : 'Email' },
                        { value: 'help', icon: HelpCircle, label: language === 'fr' ? 'Aide' : 'Help' },
                        { value: 'gift', icon: Gift, label: language === 'fr' ? 'Cadeau' : 'Gift' },
                        { value: 'headphones', icon: Headphones, label: language === 'fr' ? 'Support' : 'Support' },
                        { value: 'bell', icon: BellRing, label: language === 'fr' ? 'Cloche' : 'Bell' }
                      ].map(({ value, icon: Icon, label }) => (
                        <button
                          key={value}
                          onClick={() => updateSettings('button_icon', value)}
                          title={label}
                          className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${
                            (settings.button_icon || 'phone') === value
                              ? 'border-[#3272F1] bg-[#3272F1]/10 text-[#3272F1]'
                              : isDark 
                                ? 'border-gray-700 text-gray-400 hover:border-gray-600 hover:text-gray-300'
                                : 'border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-700'
                          }`}
                          data-testid={`icon-${value}`}
                        >
                          <Icon className="w-6 h-6" />
                          <span className="text-xs">{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Tooltip texts */}
                  <div className={`space-y-4 pt-6 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                    <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>{language === 'fr' ? 'Texte du tooltip (au survol)' : 'Tooltip text (on hover)'}</Label>
                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <Label className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Français</Label>
                        <Input
                          value={settings.tooltip_text_fr || ''}
                          onChange={(e) => updateSettings('tooltip_text_fr', e.target.value)}
                          placeholder="Besoin d'aide ? Appelez-nous !"
                          className={isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-600' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}
                          data-testid="tooltip-text-fr"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>English</Label>
                        <Input
                          value={settings.tooltip_text_en || ''}
                          onChange={(e) => updateSettings('tooltip_text_en', e.target.value)}
                          placeholder="Need help? Call us!"
                          className={isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-600' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}
                          data-testid="tooltip-text-en"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Notifications Settings */}
              {activeTab === 'notifications' && (
                <div className="space-y-8">
                  <div className={`pb-6 border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                    <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('notifications')}</h2>
                    <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>{language === 'fr' ? 'Configurez vos notifications par email' : 'Configure your email notifications'}</p>
                  </div>

                  <div className="space-y-6">
                    <div className={`flex items-center justify-between p-4 ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'} rounded-xl`}>
                      <div>
                        <Label className={isDark ? 'text-white' : 'text-gray-900'}>{t('emailEnabled')}</Label>
                        <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                          {language === 'fr' ? 'Recevoir un email à chaque demande de rappel' : 'Receive an email for each callback request'}
                        </p>
                      </div>
                      <Switch
                        checked={settings.email_enabled}
                        onCheckedChange={(checked) => updateSettings('email_enabled', checked)}
                        data-testid="email-enabled-switch"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>{t('notificationEmail')}</Label>
                      <Input
                        type="email"
                        value={settings.notification_email || ''}
                        onChange={(e) => updateSettings('notification_email', e.target.value)}
                        placeholder="vous@exemple.com"
                        className={isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-600' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}
                        data-testid="notification-email-input"
                      />
                    </div>
                  </div>

                  {/* Telegram Section */}
                  <div className={`space-y-6 pt-6 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                    <div className={`flex items-center justify-between p-4 ${isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'} border rounded-xl`}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#0088cc] rounded-lg flex items-center justify-center">
                          <Send className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <Label className={isDark ? 'text-white' : 'text-gray-900'}>Telegram</Label>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {language === 'fr' ? 'Recevoir les alertes sur Telegram' : 'Receive alerts on Telegram'}
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={settings.telegram_enabled || false}
                        onCheckedChange={(checked) => updateSettings('telegram_enabled', checked)}
                        data-testid="telegram-enabled-switch"
                      />
                    </div>

                    {settings.telegram_enabled && (
                      <div className="space-y-4">
                        {/* Step 1: Create Bot */}
                        <div className={`p-4 ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'} rounded-xl space-y-3`}>
                          <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {language === 'fr' ? 'Étape 1 : Créer votre bot' : 'Step 1: Create your bot'}
                          </h4>
                          <ol className={`text-sm space-y-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            <li className="flex items-start gap-2">
                              <span className="bg-[#0088cc] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                              <span>{language === 'fr' ? 'Ouvrez Telegram sur votre téléphone ou ordinateur' : 'Open Telegram on your phone or computer'}</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="bg-[#0088cc] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                              <span>{language === 'fr' ? 'Dans la recherche, tapez' : 'In the search bar, type'} <code className={`px-1.5 py-0.5 ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded text-xs`}>@BotFather</code> {language === 'fr' ? 'et ouvrez la conversation' : 'and open the chat'}</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="bg-[#0088cc] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                              <span>{language === 'fr' ? 'Envoyez la commande' : 'Send the command'} <code className={`px-1.5 py-0.5 ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded text-xs`}>/newbot</code></span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="bg-[#0088cc] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">4</span>
                              <span>{language === 'fr' ? 'Choisissez un nom (ex: "Alertes HICALL") puis un username (ex: alertes_hicall_bot)' : 'Choose a name (e.g. "HICALL Alerts") then a username (e.g. alertes_hicall_bot)'}</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="bg-[#0088cc] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">5</span>
                              <span>{language === 'fr' ? 'Copiez le token (ressemble à' : 'Copy the token (looks like'} <code className={`px-1.5 py-0.5 ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded text-xs`}>123456:ABC...</code>)</span>
                            </li>
                          </ol>
                          <a 
                            href="https://t.me/BotFather" 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="inline-flex items-center gap-2 text-sm text-[#0088cc] hover:underline mt-2"
                          >
                            {language === 'fr' ? 'Ouvrir @BotFather' : 'Open @BotFather'} <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>

                        <div className="space-y-2">
                          <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Bot Token</Label>
                          <Input
                            type="password"
                            value={settings.telegram_bot_token || ''}
                            onChange={(e) => updateSettings('telegram_bot_token', e.target.value)}
                            placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
                            className={isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-600 font-mono' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 font-mono'}
                            data-testid="telegram-bot-token"
                          />
                        </div>

                        {/* Step 2: Get Chat ID */}
                        <div className={`p-4 ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'} rounded-xl space-y-3`}>
                          <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {language === 'fr' ? 'Étape 2 : Récupérer votre Chat ID' : 'Step 2: Get your Chat ID'}
                          </h4>
                          <ol className={`text-sm space-y-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            <li className="flex items-start gap-2">
                              <span className="bg-[#0088cc] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                              <span>{language === 'fr' ? 'Dans Telegram, recherchez le nom de votre bot (ex: @alertes_hicall_bot)' : 'In Telegram, search for your bot name (e.g. @alertes_hicall_bot)'}</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="bg-[#0088cc] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                              <span>{language === 'fr' ? 'Cliquez sur "Démarrer" ou envoyez' : 'Click "Start" or send'} <code className={`px-1.5 py-0.5 ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded text-xs`}>/start</code></span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="bg-[#0088cc] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                              <span>{language === 'fr' ? 'Revenez ici et cliquez sur "Récupérer"' : 'Come back here and click "Get ID"'}</span>
                            </li>
                          </ol>
                        </div>

                        <div className="space-y-2">
                          <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>Chat ID</Label>
                          <div className="flex gap-2">
                            <Input
                              value={settings.telegram_chat_id || ''}
                              onChange={(e) => updateSettings('telegram_chat_id', e.target.value)}
                              placeholder="-1001234567890"
                              className={`flex-1 ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-600 font-mono' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 font-mono'}`}
                              data-testid="telegram-chat-id"
                            />
                            <Button
                              onClick={handleFetchChatId}
                              disabled={fetchingChatId || !settings.telegram_bot_token}
                              variant="outline"
                              className={isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-300 hover:bg-gray-100'}
                              data-testid="fetch-chat-id-btn"
                            >
                              {fetchingChatId ? <Loader2 className="w-4 h-4 animate-spin" /> : (language === 'fr' ? 'Récupérer' : 'Get ID')}
                            </Button>
                          </div>
                        </div>

                        <Button
                          onClick={handleTestTelegram}
                          disabled={testingTelegram || !settings.telegram_bot_token || !settings.telegram_chat_id}
                          className="w-full bg-[#0088cc] hover:bg-[#0077b5] gap-2"
                          data-testid="test-telegram-btn"
                        >
                          {testingTelegram ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              {language === 'fr' ? 'Envoi en cours...' : 'Sending...'}
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4" />
                              {language === 'fr' ? 'Tester la connexion' : 'Test connection'}
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* GDPR Settings */}
              {activeTab === 'gdpr' && (
                <div className="space-y-8">
                  <div className={`pb-6 border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                    <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('gdprSettings')}</h2>
                    <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Conformité RGPD et protection des données</p>
                  </div>

                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>{t('legalText')} (FR)</Label>
                      <Textarea
                        value={settings.popup_legal_fr}
                        onChange={(e) => updateSettings('popup_legal_fr', e.target.value)}
                        rows={3}
                        className={isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}
                        data-testid="legal-text-fr"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>{t('legalText')} (EN)</Label>
                      <Textarea
                        value={settings.popup_legal_en}
                        onChange={(e) => updateSettings('popup_legal_en', e.target.value)}
                        rows={3}
                        className={isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}
                        data-testid="legal-text-en"
                      />
                    </div>

                    <div className={`space-y-3 pt-6 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                      <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>{t('dataRetention')}</Label>
                      <div className="flex items-center gap-3">
                        <Input
                          type="number"
                          value={settings.data_retention_days}
                          onChange={(e) => updateSettings('data_retention_days', parseInt(e.target.value))}
                          className={`w-24 ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                          min={1}
                          max={365}
                          data-testid="data-retention-input"
                        />
                        <span className={isDark ? 'text-gray-500' : 'text-gray-600'}>jours</span>
                      </div>
                      <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                        Les données seront automatiquement supprimées après cette période.
                      </p>
                      
                      {/* GDPR Actions */}
                      <div className={`mt-6 p-4 ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'} rounded-xl space-y-4`}>
                        <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Actions RGPD</h4>
                        
                        <div className="flex flex-wrap gap-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              try {
                                const response = await axios.get(`${API}/gdpr/stats`, {
                                  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                });
                                const stats = response.data;
                                toast.info(
                                  `📊 Statistiques RGPD:\n` +
                                  `• Total demandes: ${stats.total_callbacks}\n` +
                                  `• À supprimer (>${stats.retention_days}j): ${stats.callbacks_to_delete}\n` +
                                  `• Plus ancienne: ${stats.oldest_callback_date ? new Date(stats.oldest_callback_date).toLocaleDateString('fr-FR') : 'Aucune'}`,
                                  { duration: 8000 }
                                );
                              } catch (error) {
                                toast.error('Erreur lors de la récupération des statistiques');
                              }
                            }}
                            className={isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : ''}
                            data-testid="gdpr-stats-btn"
                          >
                            <BarChart3 className="w-4 h-4 mr-2" />
                            Voir les statistiques
                          </Button>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              if (!window.confirm('Voulez-vous supprimer les données de plus de ' + settings.data_retention_days + ' jours ?')) return;
                              try {
                                const response = await axios.post(`${API}/gdpr/cleanup`, {}, {
                                  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                });
                                toast.success(`✅ ${response.data.deleted_count} demande(s) supprimée(s)`);
                              } catch (error) {
                                toast.error('Erreur lors du nettoyage');
                              }
                            }}
                            className={isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : ''}
                            data-testid="gdpr-cleanup-btn"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Nettoyer maintenant
                          </Button>
                        </div>
                        
                        {/* Danger Zone - Delete All Data */}
                        <div className={`mt-4 pt-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                          <p className="text-sm text-red-500 mb-3">
                            ⚠️ Zone de danger : Supprimer toutes vos données (irréversible)
                          </p>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={async () => {
                              const confirmation = window.prompt('Tapez "SUPPRIMER" pour confirmer la suppression définitive de toutes vos données et votre compte :');
                              if (confirmation !== 'SUPPRIMER') {
                                toast.error('Confirmation incorrecte');
                                return;
                              }
                              try {
                                await axios.delete(`${API}/gdpr/delete-all-data`, {
                                  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                });
                                toast.success('Compte et données supprimés');
                                localStorage.removeItem('token');
                                window.location.href = '/';
                              } catch (error) {
                                toast.error('Erreur lors de la suppression');
                              }
                            }}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="gdpr-delete-all-btn"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer mon compte et mes données
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Behavior Settings */}
              {activeTab === 'behavior' && (
                <div className="space-y-8">
                  <div className={`pb-6 border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                    <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('behaviorSettings')}</h2>
                    <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Configurez le comportement du popup</p>
                  </div>

                  <div className="space-y-6">
                    <div className={`flex items-center justify-between p-4 ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'} rounded-xl`}>
                      <div>
                        <Label className={isDark ? 'text-white' : 'text-gray-900'}>{t('triggerOnce')}</Label>
                        <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Le popup ne s'affichera qu'une seule fois par session</p>
                      </div>
                      <Switch
                        checked={settings.trigger_once_per_session}
                        onCheckedChange={(checked) => updateSettings('trigger_once_per_session', checked)}
                        data-testid="trigger-once-switch"
                      />
                    </div>

                    <div className={`space-y-3 pt-6 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                      <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>{t('inactivityTimeout')}</Label>
                      <div className="flex items-center gap-3">
                        <Input
                          type="number"
                          value={settings.inactivity_timeout_seconds}
                          onChange={(e) => updateSettings('inactivity_timeout_seconds', parseInt(e.target.value))}
                          className={`w-24 ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                          min={5}
                          max={300}
                          data-testid="inactivity-timeout-input"
                        />
                        <span className={isDark ? 'text-gray-500' : 'text-gray-600'}>secondes</span>
                      </div>
                    </div>

                    <div className={`space-y-4 pt-6 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                      <Label className={isDark ? 'text-gray-300' : 'text-gray-700'}>{t('callbackHours')}</Label>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>{t('from')}</span>
                          <Input
                            type="time"
                            value={settings.callback_hours_start}
                            onChange={(e) => updateSettings('callback_hours_start', e.target.value)}
                            className={`w-32 ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                            data-testid="callback-hours-start"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>{t('to')}</span>
                          <Input
                            type="time"
                            value={settings.callback_hours_end}
                            onChange={(e) => updateSettings('callback_hours_end', e.target.value)}
                            className={`w-32 ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                            data-testid="callback-hours-end"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Settings;
